import { CfgMtrlSourceBuffer, CfgMtrlSourceUrl } from "./CfgMtrlSource.js";
// In this file an "application" is a combination of 0..1 Material (Mtrl) and 0..m AreaTags.
// These can exist on Catalogue-level (Application Areas), Product-level, in the Feature-Options
// tree (Configuration) and for the sake of debugging, locally as debug overrides.
//
// What this file is about is reducing/aggregating/compiling this to a simple map which says that
// Area1 should have material Jeans etc. The code is written to match the behavior in CET.
//
// Side note: There is no connection in material application between products
// in a product with additional products. So there exists no methods for
// aggregating over multiple products at all.
/**
 * When there are multiple applications on the same node we need to reduce these to what is
 * actually applied. The cases where there multiple applications on the same node affecting the
 * same thing are probably user errors i Catalogue Creator. However, Catalogue Creator allows the
 * user to let them slip through, so we need to handle them the same way CET does.
 */
const reduceApplications = (applications, skipNoMtrl = false) => applications.reduce((acc, application) => {
    const { areaTags, mtrl } = application;
    // Applications which have a Mtrl but no AreaTags only have meaning on Option nodes. That
    // specific case is handled separately, so here we require AreaTags to be set.
    if (areaTags === undefined || areaTags.length === 0 || (skipNoMtrl && mtrl === undefined)) {
        return acc;
    }
    const materialSource = mtrl && {
        mtrl,
        source: application,
        overriddenByOption: false,
    };
    for (const areaTag of areaTags) {
        // The rule here is use the first found. This is opposite to the rule when
        // aggregating the tree, where the latest one found is applied
        if (acc.has(areaTag)) {
            continue;
        }
        acc.set(areaTag, materialSource);
    }
    return acc;
}, new Map());
export function aggregateAllMaterialApplications(applicationAreas, productMtrlApplications, productConfiguration, debugMtrlApplications = []) {
    const areasToMaterials = new Map();
    for (const [areaTag, mtrlSource] of reduceApplications(applicationAreas)) {
        // When Mtrl is missing there is no fallback to later Applications in the
        // same node. This is consistent with CET
        if (mtrlSource === undefined) {
            continue;
        }
        areasToMaterials.set(areaTag, mtrlSource);
    }
    // On Product level there is fallback if Mtrl is missing, so we pass true
    // to make it skip over Mtrl-less Applications
    for (const [areaTag, mtrlSource] of reduceApplications(productMtrlApplications, true)) {
        if (mtrlSource === undefined) {
            throw new Error("Should not happen");
        }
        areasToMaterials.set(areaTag, mtrlSource);
    }
    // We can not pass areasToMaterials directly to aggregateForFeature. This is because
    // an Application with AreaTags but no Mtrl is effectively a "reset". It's like saying
    // "no material". But that reset should only work for the stuff that comes from the
    // Configuration. So we must make sure we don't touch what we found from Application
    // Areas and Product.
    const configurationAreasToMaterials = new Map();
    for (const feature of productConfiguration.features) {
        // Later always beats deeper
        aggregateForFeature(feature, configurationAreasToMaterials);
    }
    for (const [areaTag, mtrlSource] of configurationAreasToMaterials) {
        areasToMaterials.set(areaTag, mtrlSource);
    }
    // Debug Applications we just let bulldozer over everything else.
    for (const debugMtrlApplication of debugMtrlApplications) {
        const { areaTags, mtrl } = debugMtrlApplication;
        if (mtrl === undefined) {
            continue;
        }
        for (const areaTag of areaTags || []) {
            areasToMaterials.set(areaTag, {
                mtrl: mtrl,
                source: debugMtrlApplication,
                overriddenByOption: false,
            });
        }
    }
    return areasToMaterials;
}
/**
 * For this Feature and it's descendants, compile the Applications into the passed areasToMaterials.
 */
function aggregateForFeature(feature, areasToMaterials, areasOpenForOverrideInOption = new Set()) {
    const featureMtrlApplications = feature._internal.mtrlApplications;
    for (const [areaTag, mtrlSource] of reduceApplications(featureMtrlApplications)) {
        // As soon as a Feature sets an areaTag that tag is allowed to have its value
        // overridden (or set if it didn't have a value from the start) by an option.
        // This option has to have an Application with a Mtrl but no AreaTags.
        // And it can only be done once - the first option found which fulfills the
        // requirements will do the override. The option does not even need to be a
        // descendant of the Feature. After this the value is fixed until
        // a Feature or an Option comes along with the AreaTag set.
        // That will then reset the process, and if it was on a Feature it opens for
        // override again.
        areasOpenForOverrideInOption.add(areaTag);
        if (mtrlSource === undefined) {
            areasToMaterials.delete(areaTag);
        }
        else {
            areasToMaterials.set(areaTag, mtrlSource);
        }
    }
    for (const option of feature.selectedOptions) {
        aggregateForOption(option, areasToMaterials, areasOpenForOverrideInOption);
    }
}
function aggregateForOption(option, areasToMaterials, areasOpenForOverrideInOption) {
    const optionMtrlApplications = option._internal.mtrlApplications;
    // The last mtrl with no areaTag can be used for areasOpenForOverrideInOption
    if (areasOpenForOverrideInOption.size !== 0) {
        // Fast backwards loop
        let i = optionMtrlApplications.length;
        while (i--) {
            const optionMtrlApplication = optionMtrlApplications[i];
            const { areaTags, mtrl } = optionMtrlApplication;
            if ((areaTags !== undefined && areaTags.length !== 0) || mtrl === undefined) {
                // Only Applications with a Mtrl but no AreaTags can be used for overrides
                continue;
            }
            const mtrlSource = {
                mtrl,
                source: optionMtrlApplication,
                overriddenByOption: true,
            };
            for (const areaTag of areasOpenForOverrideInOption) {
                areasToMaterials.set(areaTag, mtrlSource);
            }
            areasOpenForOverrideInOption.clear();
            break;
        }
    }
    // reduceApplications will filter out Applications with AreaTags. So there will
    // be no overlap between Applications used for Override and Applications used
    // for immediately setting.
    for (const [areaTag, mtrlSource] of reduceApplications(optionMtrlApplications)) {
        if (mtrlSource === undefined) {
            // An Application on an Option with no Mtrl opens up for a future option
            // to set the Mtrl (the overridden has to be an option with a Mtrl and no Area)
            areasOpenForOverrideInOption.add(areaTag);
            areasToMaterials.delete(areaTag);
        }
        else {
            areasToMaterials.set(areaTag, mtrlSource);
        }
    }
    for (const feature of option.features || []) {
        aggregateForFeature(feature, areasToMaterials, areasOpenForOverrideInOption);
    }
}
export function logMtrlSourceWithMetaDataToConsole(mtrlSourceWithMetaData) {
    const tableData = {
        Source: mtrlSourceWithMetaData.source.source,
        OverriddenByOption: mtrlSourceWithMetaData.overriddenByOption ? "Yes" : "No",
    };
    const mtrl = mtrlSourceWithMetaData.mtrl;
    if (mtrl instanceof CfgMtrlSourceUrl) {
        tableData["From prop"] = mtrl.urlIsFromProperty;
        tableData["url"] = mtrl.url;
    }
    if (mtrl instanceof CfgMtrlSourceBuffer) {
        tableData["FileName"] = mtrl.fileName;
    }
    console.table(tableData);
}
export function logMaterialMappingToConsole(areasToMaterials) {
    const sorted = Array.from(areasToMaterials);
    sorted.sort((left, right) => left[0].toLocaleLowerCase().localeCompare(right[0].toLocaleLowerCase()));
    for (const areaToMaterial of sorted) {
        const [tag, mtrlSourceWithMetaData] = areaToMaterial;
        console.groupCollapsed(`"${tag}"`);
        logMtrlSourceWithMetaDataToConsole(mtrlSourceWithMetaData);
        console.groupEnd();
    }
}
